import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, ProgressBar } from "react-bootstrap";
import swal from "sweetalert";
import { format } from "date-fns";
import DateRangePicker from "../DateRangePicker";
import { getMonthBoundaries } from "../../utils/newdate";
import { buildExportMeta } from "../../utils/export/exportMeta";

// Asks for the date range + status of an export, then loads the records
// (with progress) and builds the PDF / Excel.
//
//   exportFormat - "pdf", "excel" or null (hidden)
//   config       - the module's export config:
//     {
//       title:        "Invoice",
//       recordLabel:  "invoices",
//       statuses:     [{ value, label, includes? }],
//       fetchRecords: ({ status, startDate, endDate, filters, signal,
//                        onProgress }) => records
//       renderPdf:    ({ records, meta, signal }) => Blob
//       exportExcel:  ({ records, meta }) => Promise
//     }
//   filters      - extra values passed to fetchRecords, e.g. { currencyId }
//   defaultRange - { from, to } the date picker starts at
//   onPdfReady   - (blob, fileName) once the PDF is built
const ExportOptionsModal = ({
  exportFormat,
  onHide,
  config,
  filters,
  defaultRange,
  onPdfReady,
}) => {
  const [range, setRange] = useState({ from: null, to: null });
  const [statusValue, setStatusValue] = useState(config.statuses[0].value);
  const [progress, setProgress] = useState(null); // { label, percent|null }
  const controllerRef = useRef(null);

  const show = Boolean(exportFormat);
  const isRunning = progress !== null;
  const formatLabel = exportFormat === "pdf" ? "PDF" : "Excel";
  const status = config.statuses.find((s) => s.value === statusValue);

  // Start from the page's current date range each time the modal opens
  useEffect(() => {
    if (!show) return;
    const boundaries = getMonthBoundaries();
    setRange({
      from: defaultRange?.from ? new Date(defaultRange.from) : boundaries.start,
      to: defaultRange?.to ? new Date(defaultRange.to) : boundaries.end,
    });
    setProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleClose = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setProgress(null);
    onHide();
  };

  const handleExport = async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setProgress({ label: `Loading ${config.recordLabel}...`, percent: 0 });

    try {
      const records = await config.fetchRecords({
        status: status.value,
        startDate: format(range.from, "yyyy-MM-dd"),
        endDate: format(range.to, "yyyy-MM-dd"),
        filters,
        signal: controller.signal,
        onProgress: setProgress,
      });
      if (controller.signal.aborted) return;

      if (records.length === 0) {
        setProgress(null);
        swal({
          icon: "info",
          title: "Nothing to export",
          text: `No ${status.label} ${config.recordLabel} found in the selected date range.`,
        });
        return;
      }

      setProgress({
        label: `Generating ${formatLabel} (${records.length} ${config.recordLabel})...`,
        percent: null,
      });
      const meta = buildExportMeta(config.title, status, range.from, range.to);

      if (exportFormat === "pdf") {
        const blob = await config.renderPdf({
          records,
          meta,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        onPdfReady(blob, `${meta.fileName}.pdf`);
      } else {
        // Let the "Generating" state paint before the workbook is built
        await new Promise((resolve) => setTimeout(resolve, 50));
        await config.exportExcel({ records, meta });
      }

      controllerRef.current = null;
      setProgress(null);
      onHide();
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error(`Failed to export ${config.title} ${formatLabel}:`, error);
      setProgress(null);
      swal({
        icon: "error",
        title: "Export failed",
        text: `Something went wrong while generating the ${formatLabel}.`,
      });
    }
  };

  const radioName = `export-status-${config.title}`;

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Export to {formatLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <fieldset disabled={isRunning}>
          {range.from && (
            <DateRangePicker
              startDate={range.from}
              endDate={range.to}
              onDateRangeChange={(from, to) => setRange({ from, to })}
              label="Date Range"
            />
          )}

          <label className="form-label mt-3 d-block">Status</label>
          <div className="btn-group w-100" role="group">
            {config.statuses.map((option) => (
              <React.Fragment key={option.value}>
                <input
                  type="radio"
                  className="btn-check"
                  name={radioName}
                  id={`${radioName}-${option.value}`}
                  checked={statusValue === option.value}
                  onChange={() => setStatusValue(option.value)}
                />
                <label
                  className="btn btn-outline-primary"
                  htmlFor={`${radioName}-${option.value}`}
                >
                  {option.label}
                </label>
              </React.Fragment>
            ))}
          </div>
        </fieldset>

        {isRunning && (
          <div className="mt-4">
            <div className="small mb-1">{progress.label}</div>
            <ProgressBar
              now={progress.percent ?? 100}
              animated={progress.percent === null}
              striped={progress.percent === null}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleExport}
          disabled={isRunning || !range.from || !range.to}
        >
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportOptionsModal;
